// ProcessEnquiryPage — /skip/enquiries/:enc_id/process
//
// Full-page flow for converting a non-process_immediately enquiry to
// a job. Pre-fills every editable field from the enquiry and posts
// the merged payload to POST /api/v1/skip/enquiries/:enc_id/convert.
// On success, navigate to /skip/jobs/:new_enc_id.
//
// process_immediately enquiries (scrap_collection / load_on_site)
// stay on the inline ProcessImmediateEnquiryModal — this page is
// intentionally for the scheduled flow.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import { useSkipEnquiry, useConvertSkipEnquiry } from '../hooks/useSkipEnquiries';
import { useSkipInventory } from '../hooks/useSkipInventory';
import { useSkipSizes } from '../hooks/useSkipJobs';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import { useActivatedLegacySkipTypes } from '../hooks/useActivatedLegacySkipTypes';
import { useDriversList } from '../../admin/drivers/hooks/useDriversAdmin';

const LEGACY_LABELS: Readonly<Record<string, string>> = {
  skip: 'Skip',
  bin: 'Bin',
  roro: 'Roro',
};

import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './ProcessEnquiryPage.module.css';

type PaymentStatus = 'unpaid' | 'paid' | 'partial';
type PaymentMethod = '' | 'cash' | 'card' | 'bank_transfer' | 'account';

interface FormState {
  // Site
  site_enc_id: string | null;
  job_address_line1: string | null;
  job_address_line2: string | null;
  job_address_city: string | null;
  job_address_postcode: string | null;
  // Job
  /** Feature 2 — editable on this page. Legacy skip_type ENUM key
   *  ('skip'|'bin'|'roro'|'other'). Pre-filled from the enquiry. */
  skip_type: 'skip' | 'bin' | 'roro' | 'other' | '';
  /** Bug 2 — enc_id of a custom (non-legacy) stock type when one is
   *  chosen. skip_type stays 'other' on the persisted row. */
  custom_stock_type_uuid: string | null;
  /** Feature 2 — editable job type. enc_id of the chosen
   *  skip_job_types row. The convert payload sends `job_type` (the
   *  resolved key) since the convert endpoint accepts that today. */
  skip_job_type_enc_id: string | null;
  /** Resolved job_type key (e.g. 'delivery_only') — kept in lockstep
   *  with skip_job_type_enc_id so the immediate banner / submit body
   *  can read it without crossing the dropdown's enc_id boundary. */
  job_type: string;
  /** Encrypted enc_id of the chosen skip_stock_sizes row. */
  skip_size_enc_id: string | null;
  skip_inventory_enc_id: string;
  // Scheduling
  delivery_date: string;
  delivery_time: string;
  collection_date: string;
  collection_time: string;
  driver_uuid: string;
  // Financial
  price: string;
  payment_status: PaymentStatus;
  payment_method: PaymentMethod;
  // Notes
  notes: string;
  internal_notes: string;
  // Priority
  is_priority: boolean;
  priority_notes: string;
}

export function ProcessEnquiryPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const enquiryQuery = useSkipEnquiry(encId);
  const sizesQuery = useSkipSizes();
  // Bug 4 — size-scoped inventory. The picker is disabled until a size
  // is chosen; once set, the server filter narrows the list.
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Inventory list endpoint caps `limit` at 100 server-side; passing
  // 200 used to 400 here.
  const inventoryQuery = useSkipInventory(
    form?.skip_size_enc_id != null
      ? { status: 'available', limit: 100, skip_size_enc_id: form.skip_size_enc_id }
      : { status: 'available', limit: 100 },
  );
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const activatedLegacy = useActivatedLegacySkipTypes();
  const driversQuery = useDriversList({ page: 1, limit: 100, status: 'active' });
  const convert = useConvertSkipEnquiry(encId);

  const enquiry = enquiryQuery.data?.enquiry;

  // Resolve the enquiry's job-type metadata — drives the page heading
  // and the size dropdown filter (sizes are scoped by skip type, but
  // we don't filter inventory by type here because the user may want
  // to override the unit independently).
  const jobTypeMeta = useMemo(() => {
    if (!enquiry?.job_type) return null;
    return (jobTypesQuery.data?.jobTypes ?? []).find((t) => t.key === enquiry.job_type) ?? null;
  }, [enquiry, jobTypesQuery.data]);

  useEffect(() => {
    if (!enquiry || form !== null) return;
    // Feature 2 — pre-resolve the job-type enc_id from the enquiry's
    // saved key so the dropdown lands selected on first paint.
    const matchedJobType =
      enquiry.job_type
        ? (jobTypesQuery.data?.jobTypes ?? []).find((t) => t.key === enquiry.job_type) ?? null
        : null;
    setForm(() => ({
      // Bug 1 — when the enquiry has a saved site, pre-fill site_enc_id
      // so the convert payload carries the site_id forward; SitePicker
      // also receives initialSiteEncId for the visual selection.
      site_enc_id: enquiry.site_enc_id ?? null,
      job_address_line1: enquiry.customer_address_line1,
      job_address_line2: enquiry.customer_address_line2,
      job_address_city: enquiry.customer_city,
      job_address_postcode: enquiry.customer_postcode,
      skip_type: (enquiry.skip_type ?? '') as FormState['skip_type'],
      // Carry over a saved custom stock type so the dropdown lands
      // on the real name (not "Other") and the size filter scopes
      // by parent stock type instead of the legacy enum bucket.
      custom_stock_type_uuid:
        enquiry.skip_type === 'other' && enquiry.skip_stock_type_uuid != null
          ? enquiry.skip_stock_type_uuid
          : null,
      skip_job_type_enc_id: matchedJobType?.enc_id ?? null,
      job_type: enquiry.job_type ?? '',
      // Pre-fill skip_size_enc_id from the enquiry when present (Bug 4
      // + Bug 5 — size enc_id is now exposed on EnquirySummary). User
      // can still override; this just spares them re-entering an
      // already-known value.
      skip_size_enc_id: enquiry.skip_size_enc_id ?? null,
      skip_inventory_enc_id: '',
      delivery_date: enquiry.preferred_delivery_date ?? '',
      delivery_time: '',
      collection_date: '',
      collection_time: '',
      driver_uuid: '',
      price:
        enquiry.estimated_price === null
          ? ''
          : String(enquiry.estimated_price),
      payment_status: 'unpaid',
      payment_method: '',
      notes: enquiry.notes ?? '',
      internal_notes: '',
      is_priority: enquiry.is_priority,
      priority_notes: enquiry.priority_notes ?? '',
    }));
  }, [enquiry, form, jobTypesQuery.data]);

  if (enquiryQuery.isLoading || form === null || !enquiry) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading enquiry…" />
      </div>
    );
  }

  if (enquiryQuery.isError || !enquiryQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Enquiry not found" />
        <Card>
          <p>This enquiry could not be loaded.</p>
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/enquiries')}
          >
            Back to Enquiries
          </Button>
        </Card>
      </div>
    );
  }

  const isTerminal =
    enquiry.status === 'converted' ||
    enquiry.status === 'cancelled' ||
    enquiry.status === 'expired';

  function handleSitePickerChange(v: SitePickerValue): void {
    if (v.type === 'site') {
      setForm((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              site_enc_id: v.site_enc_id,
              job_address_line1: null,
              job_address_line2: null,
              job_address_city: null,
              job_address_postcode: null,
            },
      );
    } else if (v.type === 'one_off') {
      setForm((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              site_enc_id: null,
              job_address_line1: v.line1,
              job_address_line2: v.line2,
              job_address_city: v.city,
              job_address_postcode: v.postcode,
            },
      );
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (form === null || !enquiry) return;
    setError(null);

    // Bug 5 — required-field validation. The server enforces these for
    // non-process_immediately conversions but the client surfaces a
    // single readable banner so the user knows what to fix without a
    // round-trip. process_immediately enquiries don't reach this page.
    const missing: string[] = [];
    if (form.skip_size_enc_id === null) missing.push('Stock size');
    if (!form.skip_inventory_enc_id) missing.push('Stock unit');
    if (!form.delivery_date) missing.push('Delivery date');
    if (missing.length > 0) {
      setError(
        `Please fill in all required fields before converting: ${missing.join(
          ', ',
        )}.`,
      );
      return;
    }

    const parsedPrice = form.price.trim() === '' ? null : Number(form.price);
    if (parsedPrice !== null && Number.isNaN(parsedPrice)) {
      setError('Price must be a valid number.');
      return;
    }

    const body: Parameters<typeof convert.mutate>[0] = {
      delivery_date: form.delivery_date || null,
      delivery_time: form.delivery_time || null,
      collection_date: form.collection_date || null,
      collection_time: form.collection_time || null,
      price: parsedPrice,
      notes: form.notes.trim() || null,
      internal_notes: form.internal_notes.trim() || null,
      payment_status: form.payment_status,
      payment_method: form.payment_method || null,
      is_priority: form.is_priority,
      priority_notes:
        form.is_priority && form.priority_notes.trim()
          ? form.priority_notes.trim()
          : null,
    };
    if (form.skip_size_enc_id !== null) body.skip_size_enc_id = form.skip_size_enc_id;
    if (form.skip_inventory_enc_id) {
      body.skip_inventory_enc_id = form.skip_inventory_enc_id;
    }
    // Feature 2 — editable skip_type / job_type land on the convert
    // payload so the user can correct values picked at the enquiry
    // stage. The convert endpoint accepts skip_type + job_type and
    // (when present) overrides the saved enquiry values.
    if (form.skip_type !== '') body.skip_type = form.skip_type;
    if (form.job_type !== '') body.job_type = form.job_type;
    if (form.driver_uuid) body.driver_uuid = form.driver_uuid;
    if (form.site_enc_id !== null) {
      body.site_enc_id = form.site_enc_id;
    } else if (form.job_address_line1 && form.job_address_postcode) {
      body.site_enc_id = null;
      body.job_address_line1 = form.job_address_line1;
      body.job_address_line2 = form.job_address_line2;
      body.job_city = form.job_address_city;
      body.job_postcode = form.job_address_postcode;
    }

    convert.mutate(body, {
      onSuccess: (data) => {
        navigate(`/skip/jobs/${encodeURIComponent(data.job.enc_id)}`);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  const drivers = driversQuery.data?.drivers ?? [];
  const inventoryOptions = inventoryQuery.data?.inventory ?? [];
  // Feature 2 — sizes filter follows the form's chosen type, not the
  // enquiry's snapshot. When a custom type is selected, scope by
  // parent stock_type_enc_id (Bug 2 fields on SkipSize).
  const filteredSizes = (sizesQuery.data?.skipSizes ?? []).filter((s) =>
    form.custom_stock_type_uuid !== null
      ? s.stock_type_uuid === form.custom_stock_type_uuid
      : form.skip_type !== '' && form.skip_type !== 'other'
        ? s.skip_type === form.skip_type
        : true,
  );

  const allJobTypes = jobTypesQuery.data?.jobTypes ?? [];
  const stockTypeOptions = [
    ...activatedLegacy.types.map((t) => ({ value: t as string, label: LEGACY_LABELS[t] ?? t })),
    ...activatedLegacy.customTypes.map((c) => ({
      value: `custom:${c.uuid}`,
      label: c.name,
    })),
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Process Enquiry — ${enquiry.enquiry_number}`}
        subtitle={`${enquiry.customer_name} · ${jobTypeMeta?.name ?? enquiry.job_type ?? '—'}`}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/enquiries')}
          >
            Back
          </Button>
        }
      />

      {isTerminal ? (
        <Card>
          <p className={styles.banner}>
            This enquiry is <strong>{enquiry.status}</strong> and cannot be processed.
          </p>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={isTerminal} style={{ border: 0, padding: 0, margin: 0 }}>
          <Card>
            <h2 className={styles.sectionHeading}>Customer &amp; Site</h2>
            <div className={styles.grid}>
              <div className={[styles.field, styles.fieldFull].join(' ')}>
                <span className={styles.label}>Customer</span>
                <div className={styles.readonly}>{enquiry.customer_name}</div>
              </div>
            </div>
            <div style={{ marginTop: '0.875rem' }}>
              <SitePicker
                customerEncId={enquiry.customer_uuid}
                initialSiteUuid={enquiry.site_uuid}
                initialJobAddress={{
                  line1: enquiry.customer_address_line1,
                  line2: enquiry.customer_address_line2,
                  city: enquiry.customer_city,
                  postcode: enquiry.customer_postcode,
                }}
                onChange={handleSitePickerChange}
              />
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Job Details</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span className={styles.label}>Job Type</span>
                <select
                  className={styles.select}
                  value={form.skip_job_type_enc_id ?? ''}
                  onChange={(e) => {
                    const encIdValue = e.target.value || null;
                    const found = allJobTypes.find((t) => t.enc_id === encIdValue);
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            skip_job_type_enc_id: encIdValue,
                            job_type: found?.key ?? '',
                            // Switching job type may change scheduling
                            // semantics (process_immediately path); leave
                            // size + inventory in place though — those
                            // are bound to the physical product, not the
                            // workflow.
                          },
                    );
                  }}
                >
                  <option value="">— Select job type —</option>
                  {allJobTypes.map((t) => (
                    <option key={t.enc_id} value={t.enc_id}>
                      {t.name}
                      {t.process_immediately ? ' (Immediate)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Stock Type</span>
                <span className={styles.helper}>
                  (e.g. Skip, Bin, Roro, or your custom types)
                </span>
                <select
                  className={styles.select}
                  value={
                    form.custom_stock_type_uuid !== null
                      ? `custom:${form.custom_stock_type_uuid}`
                      : form.skip_type
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith('custom:')) {
                      const uuid = v.slice('custom:'.length);
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: 'other',
                              custom_stock_type_uuid: uuid,
                              skip_size_enc_id: null,
                              skip_inventory_enc_id: '',
                            },
                      );
                    } else {
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: v as FormState['skip_type'],
                              custom_stock_type_uuid: null,
                              skip_size_enc_id: null,
                              skip_inventory_enc_id: '',
                            },
                      );
                    }
                  }}
                >
                  <option value="">— Select stock type —</option>
                  {stockTypeOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Stock Size *</span>
                <select
                  className={styles.select}
                  required
                  value={form.skip_size_enc_id ?? ''}
                  onChange={(e) => {
                    const next = e.target.value === '' ? null : e.target.value;
                    // Bug 4: size change resets the inventory pick — the
                    // server-filtered list is about to refetch.
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            skip_size_enc_id: next,
                            skip_inventory_enc_id:
                              next === prev.skip_size_enc_id
                                ? prev.skip_inventory_enc_id
                                : '',
                          },
                    );
                  }}
                >
                  <option value="">— Select size —</option>
                  {filteredSizes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Stock Unit *</span>
                <span className={styles.helper}>
                  (e.g. skip, bin, or other inventory unit)
                </span>
                <select
                  className={styles.select}
                  required
                  disabled={form.skip_size_enc_id === null}
                  value={form.skip_inventory_enc_id}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : { ...prev, skip_inventory_enc_id: e.target.value },
                    )
                  }
                >
                  <option value="">
                    {form.skip_size_enc_id === null
                      ? '— Select stock type and size first —'
                      : '— Please assign a stock unit —'}
                  </option>
                  {inventoryOptions.map((u) => {
                    const label = [u.skip_number, u.stock_type_name, u.size_name]
                      .filter((s) => typeof s === 'string' && s.length > 0)
                      .join(' — ');
                    return (
                      <option key={u.enc_id} value={u.enc_id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Scheduling</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span className={styles.label}>Delivery date *</span>
                <input
                  className={styles.input}
                  type="date"
                  required
                  value={form.delivery_date}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, delivery_date: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Delivery time slot</span>
                <input
                  className={styles.input}
                  type="text"
                  value={form.delivery_time}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, delivery_time: e.target.value },
                    )
                  }
                  placeholder="e.g. AM, 09:00"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Collection date</span>
                <input
                  className={styles.input}
                  type="date"
                  value={form.collection_date}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, collection_date: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Collection time slot</span>
                <input
                  className={styles.input}
                  type="text"
                  value={form.collection_time}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, collection_time: e.target.value },
                    )
                  }
                />
              </label>
              <label className={[styles.field, styles.fieldFull].join(' ')}>
                <span className={styles.label}>Driver</span>
                <select
                  className={styles.select}
                  value={form.driver_uuid}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, driver_uuid: e.target.value },
                    )
                  }
                >
                  <option value="">— Unassigned —</option>
                  {drivers.map((d) => (
                    <option key={d.uuid} value={d.uuid}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Financial</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span className={styles.label}>Price</span>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, price: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Payment status</span>
                <select
                  className={styles.select}
                  value={form.payment_status}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            payment_status: e.target.value as PaymentStatus,
                          },
                    )
                  }
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Payment method</span>
                <select
                  className={styles.select}
                  value={form.payment_method}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            payment_method: e.target.value as PaymentMethod,
                          },
                    )
                  }
                >
                  <option value="">—</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="account">Account</option>
                </select>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Notes</h2>
            <div className={styles.grid}>
              <label className={[styles.field, styles.fieldFull].join(' ')}>
                <span className={styles.label}>Customer notes</span>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={form.notes}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, notes: e.target.value },
                    )
                  }
                  maxLength={5000}
                />
              </label>
              <label className={[styles.field, styles.fieldFull].join(' ')}>
                <span className={styles.label}>Internal notes (not visible to customer)</span>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={form.internal_notes}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, internal_notes: e.target.value },
                    )
                  }
                  maxLength={5000}
                />
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Priority</h2>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.is_priority}
                onChange={(e) =>
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          is_priority: e.target.checked,
                          priority_notes: e.target.checked ? prev.priority_notes : '',
                        },
                  )
                }
              />
              <span>Mark as priority</span>
            </label>
            {form.is_priority ? (
              <label className={styles.field}>
                <span className={styles.label}>Priority notes</span>
                <textarea
                  className={styles.textarea}
                  rows={2}
                  value={form.priority_notes}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : { ...prev, priority_notes: e.target.value },
                    )
                  }
                  maxLength={5000}
                />
              </label>
            ) : null}
          </Card>
        </fieldset>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/skip/enquiries')}
            disabled={convert.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              convert.isPending ||
              isTerminal ||
              form.skip_size_enc_id === null ||
              !form.skip_inventory_enc_id ||
              !form.delivery_date
            }
          >
            {convert.isPending ? 'Converting…' : 'Convert to Job'}
          </Button>
        </div>
      </form>
    </div>
  );
}
